import React, { useMemo } from 'react';
import { Link as GatsbyLink, navigate } from 'gatsby';
import { useIntl, defineMessages } from 'react-intl';
import { Button, Link } from '@printdeal/ui-library';
import clsx from 'clsx';
import type { Paths } from '../../../../types/gatsby';
import { addDataLayerEntryOnLanguageSwitch, createUrl } from './helpers';

const switchLanguage = (pagePath: string, event: React.MouseEvent<Element, MouseEvent>) => {
  if (!(event.metaKey || event.ctrlKey)) {
    event.preventDefault();
    navigate(createUrl(pagePath));
    addDataLayerEntryOnLanguageSwitch();
  }
};

const messages = defineMessages({
  'language.name.nl-nl': {
    id: 'language.name.nl-nl',
    defaultMessage: 'Dutch',
  },
  'language.name.nl-be': {
    id: 'language.name.nl-be',
    defaultMessage: 'Dutch',
  },
  'language.name.fr-be': {
    id: 'language.name.nl-fr',
    defaultMessage: 'French',
  },
});

type LanguageSwitchProps = {
  localizedPaths?: Paths;
  variant?: 'mobile' | 'desktop';
  className?: string;
};

const LanguageSwitch = ({
  localizedPaths,
  variant = 'mobile',
  className,
}: LanguageSwitchProps) => {
  const intl = useIntl();

  const pagePathLocaleAlternatives = useMemo(() => {
    if (!localizedPaths) {
      return [];
    }
    return Object.entries(localizedPaths)
      .filter(([alternateLocale]) => alternateLocale !== intl.locale)
      .map(([alternateLocale, alternatePath]) => ({
        locale: alternateLocale,
        pagePath: alternatePath,
        languageName: intl.formatMessage(
          messages[`language.name.${alternateLocale.toLowerCase() as 'nl-nl' | 'nl-be' | 'fr-be'}`],
        ),
      }));
  }, [localizedPaths, intl.locale, intl.formatMessage]);

  if (pagePathLocaleAlternatives.length === 0) {
    return null;
  }

  return (
    <div
      className={clsx(
        variant === 'desktop' && 'pd-fixed pd-right-0 pd-top-1/4 pd-z-[31] pd-origin-bottom-right -pd-rotate-90',
        className,
      )}
    >
      <ul className="pd-flex pd-gap-1">
        {pagePathLocaleAlternatives.map((language) => (
          <li key={language.pagePath}>
            {variant === 'mobile' && (
              <Link
                as={GatsbyLink}
                variant="secondary"
                to={createUrl(language.pagePath)}
                onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => (
                  switchLanguage(language.pagePath, event)
                )}
              >
                {language.languageName}
              </Link>
            )}
            {variant === 'desktop' && (
              <Button
                as={GatsbyLink}
                variant="secondary"
                to={createUrl(language.pagePath)}
                onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => (
                  switchLanguage(language.pagePath, event)
                )}
                className="pd-h-auto pd-translate-y-2 pd-rounded pd-rounded-b-0 pd-border-b-0 pd-p-3 pd-pt-1 pd-transition-transform hover:pd-translate-y-0"
              >
                {language.languageName}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default LanguageSwitch;
